import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Link2, Sparkles, AlertTriangle, Send, RefreshCw } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { displayCat } from "@/lib/readoo";

const post = (path, body) => apiFetch(`/admin/manual/${path}`, { method: "POST", auth: true, body });

// La riscrittura gira in background: interroga lo stato ogni 3 secondi (max ~4 minuti).
async function preview(url) {
  const { job_id: id } = await post("preview", { url });
  for (let i = 0; i < 80; i += 1) {
    await new Promise((r) => setTimeout(r, 3000));
    const j = await apiFetch(`/admin/manual/preview/${id}`, { auth: true });
    if (j.status === "done") return j.result;
    if (j.status === "error") throw new Error(j.error);
  }
  throw new Error("La riscrittura sta impiegando troppo, riprova");
}
const FIELD = "w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand";

function Duplicate({ draftId, dup, onDone }) {
  const [busy, setBusy] = useState(false);
  const update = async () => {
    setBusy(true);
    try {
      const r = await post("update", { draft_id: draftId, target_id: dup.article_id });
      if (r.updated) { toast.success("Aggiornamento aggiunto all'articolo"); onDone(); } else toast.info(r.message);
    } catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="manual-duplicate" className="rounded-xl border border-[#f59e0b]/50 bg-[#f59e0b]/10 p-4 space-y-3">
      <p className="flex items-center gap-2 text-sm font-semibold text-onSurface"><AlertTriangle size={16} className="text-[#f59e0b]" /> Notizia già presente sul sito: pubblicazione bloccata</p>
      <Link to={dup.path} target="_blank" data-testid="manual-duplicate-link" className="block text-sm text-brand hover:underline">{dup.title}</Link>
      {dup.can_update ? (
        <button type="button" data-testid="manual-propose-update" onClick={update} disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Aggiungi come aggiornamento
        </button>
      ) : <p className="text-xs text-subtle">L'articolo non può più ricevere aggiornamenti (oltre 24 ore o già {dup.updates} aggiornamenti).</p>}
    </div>
  );
}

function Editor({ draft, onDone }) {
  const [f, setF] = useState({ title: draft.title, summary: draft.summary, content: draft.content, category: draft.advice.category });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const publish = async () => {
    setBusy(true);
    try {
      const r = await post("publish", { draft_id: draft.draft_id, ...f });
      toast.success("Articolo pubblicato");
      onDone(r.path);
    } catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };
  const a = draft.advice;
  return (
    <div data-testid="manual-editor" className="space-y-4">
      <div data-testid="manual-advice" className="rounded-xl border border-hair bg-surfaceSecondary p-4 text-sm space-y-1.5">
        <p className="flex items-center gap-2 font-semibold text-onSurface"><Sparkles size={15} className="text-brand" /> Consiglio: {displayCat(a.category)}</p>
        <p className="text-subtle">{a.reason}</p>
        <p className="text-onSurface">Importanza: <b>{a.importance}</b> · <span className="text-subtle">{a.importance_reason}</span></p>
        <p data-testid="manual-primo-piano" className="text-onSurface">Primo Piano: <span className="text-subtle">{a.primo_piano}</span></p>
      </div>
      <label className="block text-xs font-semibold text-subtle">Categoria
        <select data-testid="manual-category" value={f.category} onChange={set("category")} className={`${FIELD} mt-1`}>
          {draft.categories.map((c) => <option key={c} value={c}>{displayCat(c)}{c === a.category ? " (consigliata)" : ""}</option>)}
        </select>
      </label>
      <label className="block text-xs font-semibold text-subtle">Titolo
        <input data-testid="manual-title" value={f.title} onChange={set("title")} className={`${FIELD} mt-1 font-semibold`} />
      </label>
      <label className="block text-xs font-semibold text-subtle">Sommario
        <textarea data-testid="manual-summary" value={f.summary} onChange={set("summary")} rows={2} className={`${FIELD} mt-1`} />
      </label>
      <label className="block text-xs font-semibold text-subtle">Testo (## per i titoletti)
        <textarea data-testid="manual-content" value={f.content} onChange={set("content")} rows={16} className={`${FIELD} mt-1 font-mono text-xs leading-relaxed`} />
      </label>
      <button type="button" data-testid="manual-publish" onClick={publish} disabled={busy}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-onBrand text-sm font-semibold disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Pubblica
      </button>
    </div>
  );
}

// Admin: da un link, articolo originale riscritto, categoria consigliata, doppioni bloccati con proposta di aggiornamento.
export function ManualArticleTab() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState("");

  const reset = () => { setDraft(null); setUrl(""); };
  const run = async (direct) => {
    setBusy(true); setDraft(null); setPublished("");
    try {
      const d = await preview(url.trim());
      if (direct && !d.duplicate) {
        const r = await post("publish", { draft_id: d.draft_id, title: d.title, summary: d.summary, content: d.content, category: d.advice.category });
        toast.success("Articolo pubblicato"); setPublished(r.path); reset();
      } else setDraft(d);
    } catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="manual-article-tab" className="max-w-3xl space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-onSurface">Scrivi da un link</h2>
        <p className="text-sm text-subtle">Incolla il link di un articolo: Readoo lo riscrive da zero, consiglia la categoria e controlla se la notizia c'è già.</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex items-center gap-2 bg-surface border border-hair rounded-full px-4 focus-within:border-brand">
          <Link2 size={15} className="text-subtle" />
          <input data-testid="manual-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="flex-1 bg-transparent py-2.5 text-sm text-onSurface focus:outline-none" />
        </div>
        <button type="button" data-testid="manual-rewrite" disabled={busy || url.length < 8} onClick={() => run(false)}
          className="px-5 py-2.5 rounded-full bg-brand text-onBrand text-sm font-semibold disabled:opacity-50">Riscrivi e controlla</button>
        <button type="button" data-testid="manual-rewrite-publish" disabled={busy || url.length < 8} onClick={() => run(true)}
          className="px-5 py-2.5 rounded-full border border-brand text-brand text-sm font-semibold disabled:opacity-50">Riscrivi e pubblica subito</button>
      </div>
      {busy && <p data-testid="manual-loading" className="flex items-center gap-2 text-sm text-subtle"><Loader2 size={15} className="animate-spin" /> Leggo e riscrivo l'articolo, può volerci fino a un minuto…</p>}
      {published && <p data-testid="manual-published" className="text-sm text-onSurface">Pubblicato: <Link to={published} target="_blank" className="text-brand hover:underline">apri l'articolo</Link></p>}
      {draft?.duplicate && <Duplicate draftId={draft.draft_id} dup={draft.duplicate} onDone={reset} />}
      {draft && !draft.duplicate && <Editor draft={draft} onDone={(p) => { setPublished(p); reset(); }} />}
    </div>
  );
}
